/**
 * Web-push subscription helper.
 *
 * Phase 1: subscribe / unsubscribe / check. The actual `push` event
 * handler in the service worker comes in Phase 2 when the cron
 * worker starts sending VAPID-signed payloads.
 *
 * iOS Safari supports web-push from 16.4+ — but only for PWAs
 * installed to the home screen. `isPushSupported()` reports
 * whether the platform can subscribe at all; `isInstalledPWA()`
 * tells us whether iOS specifically will let us subscribe even
 * if the API surface is present.
 */
import { api } from './api.js';

const VAPID_PUBLIC_KEY = import.meta.env['VITE_VAPID_PUBLIC_KEY'] as string | undefined;

// localStorage keys.
//
// `deviceId` is a stable UUID per browser profile. The server uses
// it as the upsert key so a SW replacement (which rotates the push
// endpoint on iOS) updates the existing row in place instead of
// leaving a ghost behind.
//
// `enabled` records the user's explicit intent so that after a SW
// invalidation we can silently re-subscribe on next page load
// without showing "Off — turn on to receive reminders". Without
// this flag we'd lose the distinction between "user never opted
// in" and "user opted in but the subscription was wiped".
const DEVICE_ID_KEY = 'almanac.push.deviceId';
const ENABLED_KEY = 'almanac.push.enabled';
// Set when the user dismisses the contextual "Want a push when
// this is due?" prompt that appears alongside the time picker. We
// persist this so the prompt doesn't pester after a no — the
// always-available toggle remains the path back if they change
// their mind.
const PROMPT_DISMISSED_KEY = 'almanac.push.promptDismissed';

function getDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const fresh = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, fresh);
    return fresh;
  } catch {
    // localStorage can throw in private browsing on older WebKit.
    // Falling back to a session-scoped UUID still works — the
    // server's endpoint-based fallback will recognise the device
    // by its endpoint URL on subsequent requests within the same
    // session.
    return crypto.randomUUID();
  }
}

function markEnabled(): void {
  try {
    localStorage.setItem(ENABLED_KEY, '1');
  } catch {
    // Same fallback as above — quietly ignore.
  }
}

function clearEnabled(): void {
  try {
    localStorage.removeItem(ENABLED_KEY);
  } catch {
    // Quietly ignore.
  }
}

function wasEnabled(): boolean {
  try {
    return localStorage.getItem(ENABLED_KEY) === '1';
  } catch {
    return false;
  }
}

/** Whether the user has previously opted into push reminders on
 *  this device. Independent of whether a live PushSubscription
 *  currently exists — that can be invalidated by SW replacement
 *  on iOS Safari. Used by the contextual prompt to decide whether
 *  to render at all. */
export function hasReminderIntent(): boolean {
  return wasEnabled();
}

/** Whether the user has previously dismissed the contextual
 *  "Want a push when this is due?" prompt. Once dismissed, the
 *  prompt stays hidden until the user explicitly unsubscribes
 *  (which clears the flag, since their answer to "no thanks"
 *  shouldn't outlive a re-enable). */
export function wasReminderPromptDismissed(): boolean {
  try {
    return localStorage.getItem(PROMPT_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function dismissReminderPrompt(): void {
  try {
    localStorage.setItem(PROMPT_DISMISSED_KEY, '1');
  } catch {
    // Quietly ignore — we'll just re-show the prompt on the
    // next mount, which is annoying but recoverable.
  }
}

function clearPromptDismissed(): void {
  try {
    localStorage.removeItem(PROMPT_DISMISSED_KEY);
  } catch {
    // Quietly ignore.
  }
}

/** Whether the browser exposes the Push and Notification APIs. */
export function isPushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * iOS requires the PWA to be installed to the home screen before
 * web-push is allowed; in a regular Safari tab the subscribe call
 * silently fails. Detect installed state via the standalone
 * display-mode media query (also covered by the legacy
 * `navigator.standalone` flag on iOS). Other platforms can
 * subscribe from a tab; this returns true on them too so the
 * check is non-blocking outside iOS.
 */
export function isInstalledPWA(): boolean {
  if (typeof window === 'undefined') return false;
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  const iosStandalone =
    'standalone' in window.navigator &&
    (window.navigator as { standalone?: boolean }).standalone === true;
  return standalone || iosStandalone || !isIOS();
}

function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/u.test(navigator.userAgent);
}

/**
 * Run the full opt-in flow: request permission, subscribe via the
 * browser's PushManager with the VAPID public key, and POST the
 * resulting subscription to the backend. Resolves to the
 * `PushSubscription` on success, throws with a tag-shaped message
 * on each failure mode so the caller can show a tailored hint.
 */
export async function subscribePushNotifications(): Promise<PushSubscription> {
  if (!isPushSupported()) {
    throw new Error('unsupported');
  }
  if (!VAPID_PUBLIC_KEY) {
    throw new Error('not_configured');
  }
  if (isIOS() && !isInstalledPWA()) {
    throw new Error('ios_not_installed');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('permission_denied');
  }

  const reg = await navigator.serviceWorker.ready;
  let subscription = await reg.pushManager.getSubscription();
  if (!subscription) {
    // The applicationServerKey type accepts BufferSource but TS's
    // lib.dom strictness narrows it to an ArrayBuffer-backed view;
    // our Uint8Array is technically over `ArrayBufferLike`. Cast
    // at the boundary so the type check stays clean.
    const appKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource;
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: appKey,
    });
  }

  // Send the subscription to the backend together with the user's
  // current timezone and stable device ID. The backend stores TZ
  // on the user record so the cron worker can translate `HH:MM`
  // into UTC instants; the device ID lets it recognise a future
  // re-subscribe as "same device" and update in place.
  await api.subscribePush(
    subscription,
    navigator.userAgent,
    currentTimezone(),
    getDeviceId(),
  );
  markEnabled();
  return subscription;
}

/**
 * Silent recovery for the "SW got replaced" case.
 *
 * On every SPA deploy, iOS Safari replaces the service worker and
 * invalidates the previous PushSubscription. The next time the
 * user opens the PWA, `getSubscription()` returns null even though
 * they had reminders enabled. Without recovery they'd see
 * "Off — turn on…" and have to tap a button.
 *
 * Instead: if the OS-level Notification permission is still
 * granted and we previously recorded the user's intent, request a
 * fresh subscription silently (no permission prompt, since one
 * isn't needed) and POST it to the server. The server's
 * device-ID upsert means we update the existing row instead of
 * accumulating ghosts.
 *
 * Returns the live subscription on success, or `null` if recovery
 * wasn't applicable (no prior intent, permission revoked, etc).
 */
export async function recoverPushSubscriptionIfNeeded(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  if (!VAPID_PUBLIC_KEY) return null;
  if (isIOS() && !isInstalledPWA()) return null;

  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  if (existing) return existing;

  // No subscription. Only act if the user previously opted in and
  // the OS still permits notifications — otherwise leave the UI
  // to show "Off" / "Permission denied" and let the user drive
  // the recovery.
  if (!wasEnabled()) return null;
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return null;
  }

  const appKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource;
  const fresh = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: appKey,
  });
  await api.subscribePush(
    fresh,
    navigator.userAgent,
    currentTimezone(),
    getDeviceId(),
  );
  return fresh;
}

/**
 * Full reset: clear every server-side subscription for the current
 * user, tear down whatever the browser's PushManager currently has
 * (regardless of whether we know about its endpoint), and run the
 * normal subscribe flow to produce one fresh, known-good
 * subscription. Use when the device-vs-server view has drifted —
 * typically after a PWA reinstall left stale endpoints behind.
 * Returns the count of server-side rows that were wiped.
 */
export async function resetPushNotifications(): Promise<{ deletedServerRows: number }> {
  if (!isPushSupported()) {
    throw new Error('unsupported');
  }
  if (isIOS() && !isInstalledPWA()) {
    throw new Error('ios_not_installed');
  }

  // Wipe server first so the cron stops firing to ghost endpoints
  // before the new one lands. The follow-up subscribe call below
  // re-registers the fresh endpoint.
  const { deleted } = await api.resetPushSubscriptions();

  // Drop whatever the browser currently holds, even if we never
  // POSTed it. Doing this here (rather than relying on the upcoming
  // subscribe) forces PushManager to issue a brand-new endpoint
  // instead of returning the cached one.
  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    try {
      await existing.unsubscribe();
    } catch {
      // Best-effort — proceed regardless. iOS occasionally
      // rejects unsubscribe when the underlying APNs token is
      // already gone; the next subscribe call still works.
    }
  }

  await subscribePushNotifications();
  return { deletedServerRows: deleted };
}

/** Remove the subscription both locally and on the backend. */
export async function unsubscribePushNotifications(): Promise<void> {
  if (!isPushSupported()) return;
  // Clear intent first so the silent-recovery path on next mount
  // doesn't immediately re-subscribe behind the user's back. Also
  // clear the contextual-prompt dismissal: a deliberate "turn off"
  // resets the conversation, so the prompt can ask again next time
  // the user pins a time on a task.
  clearEnabled();
  clearPromptDismissed();
  const reg = await navigator.serviceWorker.ready;
  const subscription = await reg.pushManager.getSubscription();
  if (!subscription) return;
  // Tell the backend first — if we unsubscribe the browser side
  // first and the backend request fails, the cron would keep
  // trying to push to an endpoint that's already gone. Server
  // delete is idempotent, so a stray network error here is
  // recoverable by re-running.
  try {
    await api.unsubscribePush(subscription.endpoint);
  } catch {
    // Best-effort — proceed with browser unsubscribe regardless.
  }
  await subscription.unsubscribe();
}

function currentTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

/**
 * Decode the base64url-encoded VAPID public key into the raw byte
 * form `applicationServerKey` expects. The browser pushManager
 * insists on `Uint8Array`; the standard transport format for the
 * key is base64url.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/gu, '+').replace(/_/gu, '/');
  const raw = atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    bytes[i] = raw.charCodeAt(i);
  }
  return bytes;
}
